import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllCountry,
  getStates,
  getCities,
  getAllLanguages,
} from "../../apis/util";
import { City, Country, Language, State } from "./types/utilTypes";

interface UtilState {
  countries: Country[];
  states: State[];
  cities: City[];
  languages: Language[];
  loading: boolean;
  error: string | null;
}

const initialState: UtilState = {
  countries: [],
  states: [],
  cities: [],
  languages: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchAllCountries = createAsyncThunk(
  "util/fetchAllCountries",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllCountry();
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message?.message ?? "Failed to fetch countries"
      );
    }
  }
);

export const fetchStates = createAsyncThunk(
  "util/fetchStates",
  async (countryId: string, { rejectWithValue }) => {
    try {
      const res = await getStates(countryId);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message?.message ?? "Failed to fetch states"
      );
    }
  }
);

export const fetchCities = createAsyncThunk(
  "util/fetchCities",
  async (
    { countryId, stateId }: { countryId: string; stateId: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await getCities(countryId, stateId);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message?.message ?? "Failed to fetch cities"
      );
    }
  }
);

export const fetchAllLanguages = createAsyncThunk(
  "util/fetchAllLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllLanguages();
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message?.message ?? "Failed to fetch languages"
      );
    }
  }
);

// Slice
const utilSlice = createSlice({
  name: "util",
  initialState,
  reducers: {
    resetUtilState: (state) => {
      state.countries = [];
      state.states = [];
      state.cities = [];
      state.languages = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all countries
      .addCase(fetchAllCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllCountries.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.countries = action.payload.filter((item:any) => item.name === "Nigeria");
        }
      )
      .addCase(
        fetchAllCountries.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Fetch states
      .addCase(fetchStates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStates.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.states = action.payload;
      })
      .addCase(fetchStates.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch cities
      .addCase(fetchCities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.cities = action.payload;
      })
      .addCase(fetchCities.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch all languages
      .addCase(fetchAllLanguages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllLanguages.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.languages = action.payload;
        }
      )
      .addCase(
        fetchAllLanguages.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { resetUtilState } = utilSlice.actions;

export default utilSlice.reducer;
